/**
 * JsonDocumentViewer - Renders layout_data JSON as styled HTML
 * 
 * Features:
 * - Blue highlight for text paragraphs
 * - Orange highlight for tables
 * - Click to copy text
 * - Export tables to Excel
 * - No boilerplate (filtered during ingestion)
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';

interface Block {
  id: string;
  block_type: string;
  text?: string;
  html?: string;
  markdown?: string;
  children?: Block[];
  cells?: { text: string; row_index: number; col_index: number }[];
  figure_caption?: string;
}

interface DocumentData {
  id: string;
  title: string;
  source: string;
  report_date: string;
  analyst_names: string[];
  company_tickers: string[];
  layout_data?: Block[];
  summary_html?: string;
}

const BLOCK_COLORS: Record<string, string> = {
  Text: '#e3f2fd',           // Light blue
  Paragraph: '#e3f2fd',
  SectionHeader: '#fff3e0',  // Light orange
  Title: '#fff3e0',
  Table: '#fff8e1',          // Light amber
  Figure: '#f3e5f5',         // Light purple
  Picture: '#f3e5f5',
  ListItem: '#e8f5e9',       // Light green
  ListGroup: '#e8f5e9',
};

const BLOCK_BORDER_COLORS: Record<string, string> = {
  Text: '#1976d2',           // Blue
  Paragraph: '#1976d2',
  SectionHeader: '#ef6c00',  // Orange
  Title: '#ef6c00',
  Table: '#ff8f00',          // Amber
  Figure: '#7b1fa2',         // Purple
  Picture: '#7b1fa2',
  ListItem: '#388e3c',       // Green
  ListGroup: '#388e3c',
};

const JsonDocumentViewer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const docId = searchParams.get('id') || searchParams.get('docId');
  const highlightBlockId = searchParams.get('highlightBlock');

  const [document, setDocument] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const highlightRef = useRef<HTMLDivElement>(null);

  // Fetch document
  useEffect(() => {
    if (!docId) return;

    const fetchDocument = async () => {
      try {
        setLoading(true);
        const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://127.0.0.1:8000'
          : 'https://api.wallst.co.il';
        const API_KEY = process.env.REACT_APP_API_KEY || 'psagot-api-key-2024-secure';
        const response = await fetch(`${API_BASE}/api/v3/documents/${docId}`, {
          headers: { 'X-API-Key': API_KEY },
        });
        if (!response.ok) throw new Error('Document not found');
        const data = await response.json();
        setDocument(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  // Scroll to highlighted block
  useEffect(() => {
    if (highlightRef.current && highlightBlockId) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightBlockId, document]);

  // Copy text
  const handleCopyText = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Text copied!', severity: 'success' });
  }, []);

  // Export table to Excel
  const handleExportTable = useCallback((block: Block) => {
    const cells = block.cells || [];
    if (!cells.length) {
      // Try to extract from children
      const rows: string[][] = [];
      (block.children || []).forEach(row => {
        if (row.block_type === 'TableRow') {
          const rowData = (row.children || []).map(cell => 
            cell.text || cell.html?.replace(/<[^>]+>/g, '') || ''
          );
          rows.push(rowData);
        }
      });
      
      if (!rows.length) {
        setSnackbar({ open: true, message: 'No table data found', severity: 'error' });
        return;
      }
      
      const ws = XLSX.utils.aoa_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Table');
      XLSX.writeFile(wb, `table_${Date.now()}.xlsx`);
      setSnackbar({ open: true, message: 'Table exported!', severity: 'success' });
      return;
    }

    // Build rows from cells
    const rowMap: Record<number, string[]> = {};
    cells.forEach(cell => {
      if (!rowMap[cell.row_index]) rowMap[cell.row_index] = [];
      rowMap[cell.row_index][cell.col_index] = cell.text || '';
    });
    
    const rows = Object.keys(rowMap)
      .sort((a, b) => Number(a) - Number(b))
      .map(idx => rowMap[Number(idx)]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Table');
    XLSX.writeFile(wb, `table_${Date.now()}.xlsx`);
    setSnackbar({ open: true, message: 'Table exported!', severity: 'success' });
  }, []);

  // Get block text content
  const getBlockText = (block: Block): string => {
    if (block.text) return block.text;
    if (block.html) return block.html.replace(/<[^>]+>/g, '').trim();
    if (block.markdown) return block.markdown;
    return '';
  };

  // Render a single block
  const renderBlock = (block: Block, depth: number = 0): React.ReactNode => {
    const blockType = block.block_type;
    const bgColor = BLOCK_COLORS[blockType] || '#fafafa';
    const borderColor = BLOCK_BORDER_COLORS[blockType] || '#ccc';
    const isHighlighted = block.id === highlightBlockId;
    const text = getBlockText(block);

    // Skip empty blocks and structural blocks
    if (!text && !block.children?.length && blockType !== 'Table' && blockType !== 'Figure') {
      return null;
    }

    // Skip page-level blocks, just render children
    if (blockType === 'Page') {
      return (
        <Box key={block.id} sx={{ mb: 4, pb: 2, borderBottom: '2px solid #ddd' }}>
          {block.children?.map(child => renderBlock(child, depth))}
        </Box>
      );
    }

    // Section headers
    if (blockType === 'SectionHeader' || blockType === 'Title') {
      return (
        <Typography
          key={block.id}
          ref={isHighlighted ? highlightRef : undefined}
          variant={depth === 0 ? 'h5' : 'h6'}
          sx={{
            fontWeight: 'bold',
            color: '#ef6c00',
            mt: 3,
            mb: 1,
            p: 1,
            bgcolor: isHighlighted ? '#ffeb3b' : bgColor,
            borderLeft: `4px solid ${borderColor}`,
            cursor: 'pointer',
            '&:hover': { bgcolor: '#ffe0b2' }
          }}
          onClick={() => handleCopyText(text)}
        >
          {text}
          <Tooltip title="Copy">
            <IconButton size="small" sx={{ ml: 1 }}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
      );
    }

    // Tables
    if (blockType === 'Table') {
      const cells = block.cells || [];
      const rowMap: Record<number, Record<number, string>> = {};
      cells.forEach(cell => {
        if (!rowMap[cell.row_index]) rowMap[cell.row_index] = {};
        rowMap[cell.row_index][cell.col_index] = cell.text || '';
      });
      
      const rowKeys = Object.keys(rowMap).sort((a, b) => Number(a) - Number(b));
      const colCount = Math.max(...cells.map(c => c.col_index + 1), 1);

      return (
        <Box
          key={block.id}
          ref={isHighlighted ? highlightRef : undefined}
          sx={{
            my: 2,
            p: 1,
            bgcolor: isHighlighted ? '#ffeb3b' : bgColor,
            border: `2px solid ${borderColor}`,
            borderRadius: 1,
            overflow: 'auto'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Chip label="Table" size="small" sx={{ bgcolor: borderColor, color: 'white' }} />
            <Tooltip title="Export to Excel">
              <IconButton size="small" onClick={() => handleExportTable(block)}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <tbody>
              {rowKeys.map((rowIdx, i) => (
                <tr key={rowIdx} style={{ backgroundColor: i === 0 ? '#f5f5f5' : 'white' }}>
                  {Array.from({ length: colCount }).map((_, colIdx) => (
                    <td
                      key={colIdx}
                      style={{
                        border: '1px solid #ddd',
                        padding: '8px',
                        fontWeight: i === 0 ? 'bold' : 'normal'
                      }}
                    >
                      {rowMap[Number(rowIdx)]?.[colIdx] || ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    }

    // Figures/Pictures
    if (blockType === 'Figure' || blockType === 'Picture') {
      return (
        <Box
          key={block.id}
          ref={isHighlighted ? highlightRef : undefined}
          sx={{
            my: 2,
            p: 2,
            bgcolor: isHighlighted ? '#ffeb3b' : bgColor,
            border: `2px solid ${borderColor}`,
            borderRadius: 1,
            textAlign: 'center'
          }}
        >
          <Chip label={blockType} size="small" sx={{ bgcolor: borderColor, color: 'white', mb: 1 }} />
          {block.figure_caption && (
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
              {block.figure_caption}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            [Figure content - see original PDF]
          </Typography>
        </Box>
      );
    }

    // List items
    if (blockType === 'ListItem') {
      return (
        <Box
          key={block.id}
          ref={isHighlighted ? highlightRef : undefined}
          sx={{
            pl: 3,
            py: 0.5,
            bgcolor: isHighlighted ? '#ffeb3b' : 'transparent',
            cursor: 'pointer',
            '&:hover': { bgcolor: bgColor }
          }}
          onClick={() => handleCopyText(text)}
        >
          <Typography variant="body2">• {text}</Typography>
        </Box>
      );
    }

    // Regular text/paragraphs
    if (text) {
      return (
        <Paper
          key={block.id}
          ref={isHighlighted ? highlightRef : undefined}
          elevation={0}
          sx={{
            p: 1.5,
            my: 1,
            bgcolor: isHighlighted ? '#ffeb3b' : bgColor,
            borderLeft: `4px solid ${borderColor}`,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: blockType === 'Text' ? '#bbdefb' : bgColor,
              transform: 'translateX(4px)'
            }
          }}
          onClick={() => handleCopyText(text)}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="body1" sx={{ flex: 1 }}>
              {text}
            </Typography>
            <Tooltip title="Copy text">
              <IconButton size="small">
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      );
    }

    // Container blocks with children
    if (block.children?.length) {
      return (
        <Box key={block.id} sx={{ ml: depth > 0 ? 2 : 0 }}>
          {block.children.map(child => renderBlock(child, depth + 1))}
        </Box>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!document) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="warning">Document not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2, position: 'sticky', top: 0, zIndex: 100 }}>
        <Typography variant="h5" gutterBottom>{document.title}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={document.source} size="small" />
          {document.report_date && <Chip label={document.report_date} size="small" variant="outlined" />}
          {document.company_tickers?.map(ticker => (
            <Chip key={ticker} label={ticker} size="small" color="primary" />
          ))}
          {document.analyst_names?.map(name => (
            <Chip key={name} label={name} size="small" variant="outlined" />
          ))}
        </Box>
        
        {/* Legend */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }} />
            <Typography variant="caption">Text (click to copy)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#fff8e1', border: '2px solid #ff8f00' }} />
            <Typography variant="caption">Table (export to Excel)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#fff3e0', border: '2px solid #ef6c00' }} />
            <Typography variant="caption">Header</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Summary */}
      {document.summary_html && (
        <Paper sx={{ p: 2, mb: 2, mx: 2, bgcolor: '#e8f5e9' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Summary</Typography>
          <Typography variant="body2">{document.summary_html}</Typography>
        </Paper>
      )}

      {/* Document Content */}
      <Box sx={{ px: 2, pb: 4, maxWidth: 900, mx: 'auto' }}>
        {document.layout_data?.map(block => renderBlock(block))}
        
        {!document.layout_data?.length && (
          <Alert severity="info">No layout data available for this document</Alert>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default JsonDocumentViewer;
